import type {
  CandidateRow,
  ElementOriginMapping,
  ImportRowDraft,
} from '@shared/models/element-import.model';
import type { ElementQuantityExpressionName } from '@shared/models/element-quantities.model';
import type { Baseline } from '@shared/models/project.model';
import type { ProjectChange } from './pipeline/update-engine.models';
import { candidateUnitFrom } from './materialize-candidate.helpers';
import {
  applyCandidateIfcMeasureOverride,
  type IfcMeasure,
} from './ifc-quantity.helpers';
import { buildVersionImportsChange } from './candidate-discard.helpers';

const EMPTY_DRAFT: ImportRowDraft = {};

/** Shallow equality over the flat {@link ImportRowDraft} value shape. */
const isSameDraft = (a: ImportRowDraft, b: ImportRowDraft): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (a[key as keyof ImportRowDraft] !== b[key as keyof ImportRowDraft]) {
      return false;
    }
  }
  return true;
};

/** The draft for a candidate row on a version, or an empty object when absent. */
export const getImportRowDraft = (
  version: Baseline | undefined,
  rowId: string,
): ImportRowDraft => version?.imports?.draftsByRowId?.[rowId] ?? EMPTY_DRAFT;

/**
 * True when the draft has a product or recipe mapping. Conversion/amount
 * leftovers alone are not a mapping — "Remove mapping" stays hidden.
 */
export const importRowDraftHasRemovableMapping = (
  draft: ImportRowDraft,
): boolean => Boolean(draft.referenceId);

/**
 * Draft left after Remove mapping: keeps only {@link ImportRowDraft.ifcMeasure}
 * when set so the file quantity source survives clearing the product mapping.
 */
export const draftAfterRemovingMapping = (
  draft: ImportRowDraft,
): ImportRowDraft | null =>
  draft.ifcMeasure ? { ifcMeasure: draft.ifcMeasure } : null;

/**
 * Promote a draft to the {@link ElementOriginMapping} shape (mirrors a DB
 * mapping row) for the materialize overlay. Returns `undefined` when no
 * reference is set yet — an unmapped row can't materialize.
 */
export const draftToOriginMapping = (
  draft: ImportRowDraft,
  unitFrom: string,
): ElementOriginMapping | undefined => {
  if (!draft.referenceId) return undefined;
  return {
    referenceId: draft.referenceId,
    referenceType: draft.referenceType ?? 'product',
    mappings: {
      [unitFrom]: {
        unitTo: draft.unitTo,
        quantityFactor: draft.quantityFactor,
        quantityName: draft.selectedQuantity,
      },
    },
  };
};

/** Per-row draft-mapping overlay for a set of rows, read from the version. */
export const buildDraftMappingsByRowId = (
  version: Baseline | undefined,
  rows: readonly CandidateRow[],
): Record<string, ElementOriginMapping | undefined> => {
  const out: Record<string, ElementOriginMapping | undefined> = {};
  for (const row of rows) {
    const draft = getImportRowDraft(version, row.id);
    const effectiveRow = applyCandidateIfcMeasureOverride(
      row,
      draft.ifcMeasure,
    );
    const unitFrom = candidateUnitFrom(effectiveRow);
    if (!unitFrom) continue;
    const mapping = draftToOriginMapping(draft, unitFrom);
    if (mapping) out[row.id] = mapping;
  }
  return out;
};

/** Per-row selected-quantity overlay for a set of rows, read from the version. */
export const buildSelectedQuantitiesByRowId = (
  version: Baseline | undefined,
  rows: readonly CandidateRow[],
): Record<string, ElementQuantityExpressionName | undefined> => {
  const out: Record<string, ElementQuantityExpressionName | undefined> = {};
  for (const row of rows) {
    const selected = getImportRowDraft(version, row.id).selectedQuantity;
    if (selected) out[row.id] = selected;
  }
  return out;
};

/** Per-row IFC measure override overlay for materialization (DEV-3577). */
export const buildIfcMeasureByRowId = (
  version: Baseline | undefined,
  rows: readonly CandidateRow[],
): Record<string, IfcMeasure | undefined> => {
  const out: Record<string, IfcMeasure | undefined> = {};
  for (const row of rows) {
    const measure = getImportRowDraft(version, row.id).ifcMeasure;
    if (measure) out[row.id] = measure;
  }
  return out;
};

/**
 * Build the {@link ProjectChange} that writes a candidate row's draft.
 * Shallow-merges `patch` into the existing draft; pass `null` to clear the draft
 * entirely. Returns `undefined` when the write is a no-op (nothing changed).
 */
export const buildImportRowDraftChange = (
  version: Baseline,
  rowId: string,
  patch: Partial<ImportRowDraft> | null,
): ProjectChange | undefined =>
  buildImportRowDraftsChange(version, {
    [rowId]:
      patch === null
        ? { mode: 'replace', draft: null }
        : { mode: 'merge', patch },
  });

/**
 * One row's draft write: shallow-merged into the existing draft, or replacing
 * it wholesale (`null` clears it).
 */
export type ImportRowDraftWrite =
  | { mode: 'merge'; patch: Partial<ImportRowDraft> }
  | { mode: 'replace'; draft: ImportRowDraft | null };

/**
 * Plural {@link buildImportRowDraftChange}: applies every write to one copy of
 * `draftsByRowId` and emits a *single* `VersionImports` change. Bulk edits must
 * go through this — looping the singular builders rebuilds the imports object
 * from the same `version` each time, so every change but the last is clobbered.
 * Merge and replace writes mix freely in one batch. Returns `undefined` when no
 * write changed anything.
 */
export const buildImportRowDraftsChange = (
  version: Baseline,
  writesByRowId: Record<string, ImportRowDraftWrite>,
): ProjectChange | undefined => {
  const current = version.imports?.draftsByRowId ?? {};
  const draftsByRowId: Partial<Record<string, ImportRowDraft>> = { ...current };
  let changed = false;

  for (const [rowId, write] of Object.entries(writesByRowId)) {
    const previous = current[rowId];
    const next =
      write.mode === 'merge'
        ? { ...(previous ?? EMPTY_DRAFT), ...write.patch }
        : write.draft;

    if (next === null) {
      if (previous === undefined) continue;
      delete draftsByRowId[rowId];
      changed = true;
      continue;
    }

    // Drafts feed undo/redo now: skip identical writes so re-selecting the same
    // mapping doesn't push an empty history frame. A merge onto a row with no
    // draft compares against the empty draft, so a patch of nothing but
    // `undefined` values stays a no-op; a replace of an absent draft is a real
    // write and must not be swallowed.
    const baseline =
      write.mode === 'merge' ? (previous ?? EMPTY_DRAFT) : previous;
    if (baseline !== undefined && isSameDraft(baseline, next)) continue;
    draftsByRowId[rowId] = next;
    changed = true;
  }

  if (!changed) return undefined;

  return buildVersionImportsChange(version, { draftsByRowId });
};

/**
 * Build the {@link ProjectChange} that *replaces* a candidate row's draft with
 * `draft` (or clears it when `draft` is `null`) — unlike
 * {@link buildImportRowDraftChange}, which shallow-merges. Lets a caller fold a
 * full draft replacement into the same batch as its element changes (one undo
 * frame). Returns `undefined` when the write is a no-op.
 */
export const buildImportRowDraftReplaceChange = (
  version: Baseline,
  rowId: string,
  draft: ImportRowDraft | null,
): ProjectChange | undefined =>
  buildImportRowDraftsChange(version, {
    [rowId]: { mode: 'replace', draft },
  });
